import { useState, useCallback, useMemo } from 'react';
import { useDebounce } from '@/hooks';
import { isValidUsState } from '@/feature/patients/utils/us-state';
import { useSearchProvidersInfinite } from './providerAPI';
import {
  SearchProvidersParams,
  ProviderType,
  ProviderSearchResult,
} from '../types';

export const useProviderSearch = () => {
  const [searchFields, setSearchFields] = useState({
    providerName: '',
    facilityName: '',
    city: '',
    state: '',
    postalCode: '',
  });

  const [isFormBeingPopulated, setIsFormBeingPopulated] = useState(false);

  const debouncedProviderName = useDebounce(searchFields.providerName, 300);
  const debouncedFacilityName = useDebounce(searchFields.facilityName, 300);
  const debouncedCity = useDebounce(searchFields.city, 300);
  const debouncedState = useDebounce(searchFields.state, 300);
  const debouncedPostalCode = useDebounce(searchFields.postalCode, 300);

  const debouncedFields = useMemo(
    () => ({
      providerName: debouncedProviderName.trim(),
      facilityName: debouncedFacilityName.trim(),
      city: debouncedCity.trim(),
      state: debouncedState.trim(),
      postalCode: debouncedPostalCode.trim(),
    }),
    [
      debouncedProviderName,
      debouncedFacilityName,
      debouncedCity,
      debouncedState,
      debouncedPostalCode,
    ],
  );

  // Determine search criteria and build search parameters (without pagination)
  const baseSearchParams:
    | Omit<SearchProvidersParams, 'pagination'>
    | undefined = useMemo(() => {
    const hasProviderName = debouncedFields.providerName.length >= 3;
    const hasFacilityName = debouncedFields.facilityName.length >= 3;
    const hasCity = debouncedFields.city.length >= 2;
    const hasState = isValidUsState(debouncedFields.state);
    const hasValidZip = /^[0-9]{5}$/.test(debouncedFields.postalCode); // Exactly 5 digits

    // City/State search requires BOTH to be present
    const hasCityState = hasCity && hasState;

    // Need at least one valid search criteria
    if (!hasProviderName && !hasFacilityName && !hasCityState && !hasValidZip) {
      return undefined;
    }

    const useFacilityName = hasFacilityName;
    const useProviderName = hasProviderName && !hasFacilityName;
    const shouldSearchIndividuals = useProviderName || useFacilityName;

    const newParams = {
      providerType: (shouldSearchIndividuals
        ? 'individual'
        : 'facility') as ProviderType,
      ...(useProviderName && { providerName: debouncedFields.providerName }),
      ...(useFacilityName && { facilityName: debouncedFields.facilityName }),
      ...(hasCityState &&
        !useProviderName && {
          city: debouncedFields.city,
          state: debouncedFields.state,
        }),
      ...(hasValidZip &&
        !useProviderName && { postalCode: debouncedFields.postalCode }),
    };

    // During form population, return undefined to disable query
    const result = isFormBeingPopulated ? undefined : newParams;
    return result;
  }, [isFormBeingPopulated, debouncedFields]);

  const {
    data: infiniteQueryData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useSearchProvidersInfinite(baseSearchParams);

  // Flatten all pages into a single array of results
  const allResults: ProviderSearchResult[] = useMemo(() => {
    if (!infiniteQueryData?.pages) return [];
    return infiniteQueryData.pages.reduce((acc, page) => {
      return [...acc, ...page.data];
    }, [] as ProviderSearchResult[]);
  }, [infiniteQueryData?.pages]);

  /**
   * Updates a search field when user types in form
   * Only updates if form is not being populated by provider selection
   */
  const handleFieldChange = useCallback(
    (field: keyof typeof searchFields, value: string) => {
      if (!isFormBeingPopulated) {
        setSearchFields((prev) => ({ ...prev, [field]: value }));
      }
    },
    [isFormBeingPopulated],
  );

  /**
   * Clears all search fields (typically called when provider is selected)
   */
  const clearAllSearchFields = useCallback(() => {
    setSearchFields({
      providerName: '',
      facilityName: '',
      city: '',
      state: '',
      postalCode: '',
    });
  }, []);

  /**
   * Prevents search triggers while form is being populated with selected provider data
   */
  const lockSearchDuringFormPopulation = useCallback(() => {
    setIsFormBeingPopulated(true);
  }, []);

  /**
   * Re-enables search triggers after form population is complete.
   */
  const unlockSearchAfterFormPopulation = useCallback(() => {
    setIsFormBeingPopulated(false);
  }, []);

  /**
   * Loads the next page of results
   */
  const loadMoreResults = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const hasMoreResults = hasNextPage && !isFetchingNextPage;

  const hasSearchParams = useMemo(() => {
    return baseSearchParams !== undefined;
  }, [baseSearchParams]);

  return {
    searchResults: allResults,
    isSearching: isFormBeingPopulated ? false : isLoading || isFetchingNextPage,
    hasMoreResults,
    hasSearchParams,
    loadMoreResults,
    handleFieldChange,
    clearAllSearchFields,
    lockSearchDuringFormPopulation,
    unlockSearchAfterFormPopulation,
  };
};
